import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import api from './api/axiosInstance';
import base_url from './api/Config';
import TopBar from './component/TopBar';

function Approved() {
  const [loanDetails, setLoanDetails] = useState(null);
  const [otp, setOtp] = useState('');
  const [isEsignChecked, setEsignChecked] = useState(false);
  const [isAutoDebitChecked, setAutoDebitChecked] = useState(false);
  const [showEsignModal, setShowEsignModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');

  const location = useLocation();
  const navigate = useNavigate();
  const id = new URLSearchParams(location.search).get('id');

  useEffect(() => {
    if (!id) return;
    api.get(`${base_url}/api/userProduct/application`, { params: { id } })
      .then(res => setLoanDetails(res.data))
      .catch(() => {});
  }, [id]);

  const handleEsignOpen = async () => {
    try {
      await api.post(`${base_url}/api/eSign/generateESignOtp?id=${id}`);
      setShowEsignModal(true);
    } catch {
      alert('Failed to generate OTP. Please try again.');
    }
  };

  const handleOtpSubmit = async () => {
    if (!otp.trim()) { alert('Please enter the OTP.'); return; }
    try {
      const res = await api.post(
        `${base_url}/api/eSign/verifyAndSetDeviceId?id=${id}&enteredOtp=${otp}&deviceId=Geetanjali`,
        null, { headers: { 'Content-Type': 'application/json' } }
      );
      if (res.status === 200 && res.data === 'OTP verified successfully, and device ID set') {
        setEsignChecked(true);
        setShowEsignModal(false);
        setOtp('');
      } else {
        alert('OTP verification failed.');
      }
    } catch {
      alert('Error verifying OTP.');
    }
  };

  const handleAutoDebit = async () => {
    setIsLoading(true);
    try {
      await api.post(`${base_url}/api/cashFree/autoUpi?id=${id}`, {
        customerName: loanDetails?.name || '',
        customerPhone: loanDetails?.mobileNo || '',
        customerEmail: loanDetails?.emailId || '',
        returnUrl: `${base_url}/Approved?id=${id}`,
        maxAmount: loanDetails?.tob || '',
        maxCycles: loanDetails?.noOfInstalment || '',
      }, { headers: { 'Content-Type': 'application/json' } });
      setAutoDebitChecked(true);
      setMessage('Auto Debit verified successfully.');
    } catch {
      alert('Auto-Debit verification failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const infoRows = loanDetails ? [
    { label: 'Loan Amount', value: `₹${loanDetails.loanAmount} /-` },
    { label: 'Tenure', value: `${loanDetails.tenure} Days` },
    { label: 'Interest Rate', value: `${loanDetails.intRate}%` },
    { label: 'Processing Fee', value: `₹${loanDetails.processFee} /-` },
    { label: 'APR', value: `${loanDetails.apr}%` },
    { label: 'EMI Amount', value: `₹${loanDetails.emiAmount} /-` },
  ] : [];

  return (
    <div className="app-page">
      <TopBar title="Approved Loan" backPath={`/CustomerEmiPage?id=${id}`} />
      {isLoading && <div className="loader-overlay"><div className="spinner-border text-light" /></div>}

      <div className="app-content">
        <div className="app-card">
          <p className="section-title">Loan Summary</p>
          {!loanDetails ? (
            <p style={{ color: '#888', textAlign: 'center' }}>Loading...</p>
          ) : (
            infoRows.map((row, i) => (
              <div key={i} className="info-row">
                <span>{row.label}</span>
                <strong>{row.value}</strong>
              </div>
            ))
          )}
        </div>

        {message && (
          <div style={{ background: '#e8f5e9', borderRadius: 10, padding: 12, marginTop: 4, color: '#2e7d32', fontWeight: 600, fontSize: 13 }}>
            {message}
          </div>
        )}

        <div className="app-card">
          <p className="section-title">Actions Required</p>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>E-Sign</p>
              <p style={{ fontSize: 12, color: '#888', marginBottom: 0 }}>Digitally sign your loan agreement</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" checked={isEsignChecked} readOnly style={{ width: 18, height: 18 }} />
              <button
                onClick={handleEsignOpen}
                disabled={isEsignChecked}
                style={{
                  background: isEsignChecked ? '#e0e0e0' : '#1d2e77', color: '#fff',
                  border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer'
                }}
              >
                E-Sign
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>Auto-Debit</p>
              <p style={{ fontSize: 12, color: '#888', marginBottom: 0 }}>Set up automatic EMI deduction</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" checked={isAutoDebitChecked} readOnly style={{ width: 18, height: 18 }} />
              <button
                onClick={handleAutoDebit}
                disabled={isAutoDebitChecked}
                style={{
                  background: isAutoDebitChecked ? '#e0e0e0' : '#1d2e77', color: '#fff',
                  border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer'
                }}
              >
                Activate
              </button>
            </div>
          </div>
        </div>

        <button
          className="btn-primary-app"
          disabled={!isEsignChecked || !isAutoDebitChecked}
          onClick={() => navigate(`/LoanInfo?id=${id}`)}
        >
          Proceed
        </button>
      </div>

      <Modal show={showEsignModal} onHide={() => setShowEsignModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>E-Sign Verification</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>Enter the OTP sent to your registered mobile number.</p>
          <div className="field-group">
            <label className="field-label">OTP</label>
            <input
              className="field-input"
              type="password"
              maxLength={6}
              value={otp}
              onChange={e => setOtp(e.target.value)}
              style={{ textAlign: 'center', letterSpacing: 8, fontSize: 20 }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setShowEsignModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleOtpSubmit}>Verify</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Approved;
